import React, { useState } from 'react';

import { useSelector } from 'react-redux';

import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { Title } from 'features/App';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import useSkuVenues from '../shared/useSkuVenues';

import {
  buildSearchSuggestions,
  collectFilterOptions,
  filterItems,
  toItemsReportFilter,
  UNCATEGORIZED_KEY,
} from './SkuItemReports.filters';
import { useItemsReportQuery } from './SkuItemReports.hooks';
import messages from './SkuItemReports.messages';
import SkuItemReportsLocalFilters from './SkuItemReportsLocalFilters';
import SkuItemReportsTable from './SkuItemReportsTable';
import { DATE_RANGE_PRESET, findMatchingPreset, presetRange } from './SkuOrderReports.filters';
import SkuOrderReportsFilters from './SkuOrderReportsFilters';
import SummaryTile, { SummaryTileEntries } from './SkuReportsSummaryTile';
import './SkuItemReports.scss';

const { block, element } = bem('sku-item-reports');

const EMPTY_VALUE = '—';

const SkuItemReportsPage = () => {
  const isMobile = useSelector(selectIsMobile);
  const skuVenues = useSkuVenues();
  const showLocationColumn = skuVenues.length > 1;

  const [selectedVenueIds, setSelectedVenueIds] = useState(null);
  const [dateFilter, setDateFilter] = useState(() => ({
    preset: DATE_RANGE_PRESET.LAST_7_DAYS,
    range: presetRange(DATE_RANGE_PRESET.LAST_7_DAYS),
  }));
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [search, setSearch] = useState('');

  const venueIds = selectedVenueIds ?? skuVenues.map(({ venueId }) => venueId);
  const filter = toItemsReportFilter({ venueIds, range: dateFilter.range });

  const { data: report, isInitialLoading, isError, refetch } = useItemsReportQuery(filter);

  const allItems = report?.items || [];
  const filterOptions = collectFilterOptions(allItems);
  const categories = selectedCategories.filter(
    category =>
      filterOptions.categories.includes(category) || (category === UNCATEGORIZED_KEY && filterOptions.hasUncategorized)
  );
  const menus = selectedMenus.filter(menu => filterOptions.menus.includes(menu));
  const items = filterItems(allItems, { categories, menus, search });
  const suggestions = buildSearchSuggestions(filterItems(allItems, { categories, menus }), search);

  // Sellers come one per currency, and a units-only subtitle can't tell the entries apart, so the
  // subtitle names the currency — but only then, keeping the usual single-currency card on-design.
  const hasMultipleSellerCurrencies = (report?.topSellers?.length || 0) > 1;

  const renderTileValue = content => {
    if (isInitialLoading) return <LoadingDots />;
    if (!content) return EMPTY_VALUE;
    return content;
  };

  return (
    <>
      <Message message={messages.pageTitle} as={Title} />

      <div className={block()}>
        <SkuOrderReportsFilters
          venues={skuVenues}
          selectedVenueIds={venueIds}
          onVenuesChange={setSelectedVenueIds}
          range={dateFilter.range}
          onRangeChange={range => setDateFilter({ preset: findMatchingPreset(range), range })}
          preset={dateFilter.preset}
          onPresetSelect={preset => setDateFilter({ preset, range: presetRange(preset) })}
        />

        <div className={element('tiles')}>
          <SummaryTile
            label={<Message message={messages.topSellerTile} />}
            value={renderTileValue(
              report?.topSellers?.length ? (
                <SummaryTileEntries
                  entries={report.topSellers}
                  renderSubtitle={entry =>
                    hasMultipleSellerCurrencies ? (
                      <Message
                        message={messages.topSellerSubtitleWithCurrency}
                        values={{ count: entry.unitsSold, currency: entry.earnings?.currencyCode }}
                      />
                    ) : (
                      <Message message={messages.topSellerSubtitle} values={{ count: entry.unitsSold }} />
                    )
                  }
                />
              ) : null
            )}
          />
          <SummaryTile
            label={<Message message={messages.topEarnerTile} />}
            value={renderTileValue(
              report?.topEarners?.length ? (
                <SummaryTileEntries
                  entries={report.topEarners}
                  renderSubtitle={entry => (
                    <Message
                      message={messages.topEarnerSubtitle}
                      values={{
                        amount: (
                          <Message
                            format="currency"
                            currency={entry.earnings?.currencyCode}
                            value={entry.earnings?.amount}
                          />
                        ),
                      }}
                    />
                  )}
                />
              ) : null
            )}
          />
        </div>

        <div className={element('listing-header')}>
          <span className={element('listing-count')}>
            {report && <Message message={messages.itemsCount} values={{ count: items.length }} />}
          </span>
        </div>

        <SkuItemReportsLocalFilters
          options={filterOptions}
          categories={categories}
          onCategoriesChange={setSelectedCategories}
          menus={menus}
          onMenusChange={setSelectedMenus}
          search={search}
          onSearchChange={setSearch}
          suggestions={suggestions}
        />

        <SkuItemReportsTable
          items={items}
          filtersKey={JSON.stringify([filter, categories, menus, search])}
          showLocationColumn={showLocationColumn}
          isMobile={isMobile}
          isLoading={isInitialLoading}
          isError={isError}
          onRetry={() => refetch()}
        />
      </div>
    </>
  );
};

export default SkuItemReportsPage;
